import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { ChevronDown, Download, MessageSquare } from 'lucide-react';
import { useState } from 'react';
import { Checkbox, ErrorBanner, Textarea } from '@/components/form';
import { FileDrop, StatusChip } from '@/components/migration/file-drop';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { downloadReport } from '@/features/reports/api';
import { DrillLink } from '@/features/reports/report-table';
import type { Drill } from '@/features/reports/types';
import { api } from '@/lib/api';
import { useMe } from '@/lib/clients';
import { formatDate, formatDateTime } from '@/lib/format';
import {
  RUN_REPORTS,
  runReports,
  fmtAmount,
  isZero,
  readUpload,
  runLabel,
  useParallelRun,
  visibleLines,
  type ComparisonLine,
  type ComparisonReport,
  type ParallelRunView,
  type RunReport,
} from '@/lib/migration';
import { useOrgId } from '@/lib/org';
import { cn } from '@/lib/utils';
import { ltr } from '@/lib/arabic';
import { i18n, useLanguage, useT } from '@/lib/i18n';

export const Route = createFileRoute('/reports/parallel-run/$runId')({
  component: ParallelRunPage,
});

const reportTitle = (key: RunReport): string =>
  i18n.t(`reportPages.dashboard.parallelRun.report.${key}`);

/** A whole month's name in Arabic ("أكتوبر 2026", Western digits); a range stays as dates. */
const runLabelFor = (lang: 'en' | 'ar', label: string, from: string): string => {
  if (lang !== 'ar' || label.includes('/')) return label;
  const [y, m] = from.split('-').map(Number) as [number, number];
  return new Intl.DateTimeFormat('ar-AE-u-nu-latn', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, m - 1, 1)));
};

/** Screen R3 (document 06): one month of the parallel run, QuickBooks beside Outss Ledger. */
function ParallelRunPage() {
  const { runId } = Route.useParams();
  const orgId = useOrgId();
  const q = useParallelRun(runId);
  const t = useT();
  return !orgId ? (
    <EmptyState title={t('shell.selectClient')} hint={t('home.selectClientHint')} />
  ) : q.isPending ? (
    <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
  ) : q.isError ? (
    <EmptyState title={t('reportPages.dashboard.parallelRun.loadError')} hint={q.error.message} />
  ) : (
    <Month key={runId} run={q.data} />
  );
}

function Month({ run }: { run: ParallelRunView }) {
  const orgId = useOrgId()!;
  const t = useT();
  const lang = useLanguage();
  const me = useMe();
  const qc = useQueryClient();
  const staff = Boolean(me.data?.isStaff);
  const signed = Boolean(run.signedOffAt);
  const editable = staff && !signed;
  const firstUploaded = run.reports.find((r) => r.uploaded)?.report ?? 'trial_balance';
  const [tab, setTab] = useState<RunReport>(firstUploaded);
  const [onlyDiff, setOnlyDiff] = useState(false);
  const set = (v: ParallelRunView) => {
    qc.setQueryData(['parallel-run', orgId, run.id], v);
    void qc.invalidateQueries({ queryKey: ['parallel-runs', orgId] });
  };
  const signOff = useMutation({
    mutationFn: () =>
      api<ParallelRunView>(`/orgs/${orgId}/parallel-runs/${run.id}/sign-off`, {
        method: signed ? 'DELETE' : 'POST',
        ...(signed ? {} : { body: JSON.stringify({}) }),
      }),
    onSuccess: (v) => {
      set(v);
      toast(
        v.signedOffAt
          ? t('reportPages.dashboard.parallelRun.monthSignedOff')
          : t('reportPages.dashboard.parallelRun.signOffWithdrawn'),
      );
    },
    onError: (e) => toast((e as Error).message, { tone: 'error' }),
  });
  const exportAs = async (format: 'xlsx' | 'pdf' | 'csv') => {
    try {
      await downloadReport(orgId, 'parallel-run', { from: run.from, to: run.to }, format);
    } catch (e) {
      toast((e as Error).message, { tone: 'error' });
    }
  };
  const current = run.reports.find((r) => r.report === tab)!;
  return (
    <>
      <PageHeader
        title={t('reportPages.dashboard.parallelRun.title', {
          period: runLabelFor(lang, runLabel(run.from, run.to), run.from),
        })}
        description={t('reportPages.dashboard.parallelRun.description', {
          from: ltr(formatDate(run.from)),
          to: ltr(formatDate(run.to)),
        })}
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to="/reports/parallel-run">
                {t('reportPages.dashboard.parallelRun.allMonths')}
              </Link>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  <Download className="size-4" /> {t('reportPages.dashboard.parallelRun.export')}{' '}
                  <ChevronDown className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => void exportAs('xlsx')}>Excel</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => void exportAs('pdf')}>PDF</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => void exportAs('csv')}>CSV</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {staff ? (
              <Button
                variant={signed ? 'outline' : 'default'}
                disabled={signOff.isPending || (!signed && !run.canSignOff)}
                title={
                  !signed && !run.canSignOff
                    ? run.uploaded
                      ? t('reportPages.dashboard.parallelRun.explainFirst')
                      : t('reportPages.dashboard.parallelRun.uploadTbFirst')
                    : undefined
                }
                onClick={() => signOff.mutate()}
              >
                {signed
                  ? t('reportPages.dashboard.parallelRun.withdrawSignOff')
                  : t('reportPages.dashboard.parallelRun.signOff')}
              </Button>
            ) : null}
          </>
        }
      />
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-3 text-sm" role="status">
          {signed ? (
            <StatusChip tone="success">
              {run.signedOffBy
                ? t('reportPages.dashboard.parallelRun.signedOffOnBy', {
                    date: formatDateTime(run.signedOffAt!),
                    name: run.signedOffBy,
                  })
                : t('reportPages.dashboard.parallelRun.signedOffOn', {
                    date: formatDateTime(run.signedOffAt!),
                  })}
            </StatusChip>
          ) : (
            <StatusChip tone="info">{t('reportPages.dashboard.parallelRun.open')}</StatusChip>
          )}
          <span>
            {run.differences === 0
              ? t('reportPages.dashboard.parallelRun.noDifferences')
              : t('reportPages.dashboard.parallelRun.differencesUnexplained', {
                  count: run.differences,
                  unexplained: run.unexplained,
                })}
          </span>
          {signed ? (
            <span className="text-muted-foreground">
              {t('reportPages.dashboard.parallelRun.withdrawToChange')}
            </span>
          ) : null}
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {runReports().map((r) => (
            <UploadCard
              key={r.key}
              run={run}
              meta={r}
              report={run.reports.find((x) => x.report === r.key)!}
              editable={editable}
              onDone={(v) => {
                set(v);
                setTab(r.key);
              }}
            />
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-1 border-b" role="tablist">
          {run.reports.map((r) => (
            <button
              key={r.report}
              role="tab"
              aria-selected={tab === r.report}
              disabled={!r.uploaded}
              onClick={() => setTab(r.report)}
              className={cn(
                '-mb-px border-b-2 px-3 py-2 text-sm',
                tab === r.report
                  ? 'border-primary font-medium text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
                !r.uploaded && 'cursor-not-allowed opacity-50',
              )}
            >
              {reportTitle(r.report)}
              {r.uploaded && r.differences ? (
                <span className="ms-1 rounded-full bg-warning/15 px-1.5 text-xs text-warning">
                  {r.differences}
                </span>
              ) : null}
            </button>
          ))}
          <Checkbox
            className="ms-auto pe-2"
            label={t('reportPages.dashboard.parallelRun.onlyDifferences')}
            checked={onlyDiff}
            onChange={(e) => setOnlyDiff(e.target.checked)}
          />
        </div>
        {current.uploaded ? (
          <Comparison
            run={run}
            report={current}
            onlyDiff={onlyDiff}
            editable={editable}
            onSaved={set}
          />
        ) : (
          <EmptyState
            title={t(`reportPages.dashboard.parallelRun.uploadToCompare.${current.report}`)}
            hint={t(`reportPages.dashboard.parallelRun.whereInQbo.${current.report}`)}
          />
        )}
      </div>
    </>
  );
}

function UploadCard({
  run,
  meta,
  report,
  editable,
  onDone,
}: {
  run: ParallelRunView;
  meta: (typeof RUN_REPORTS)[number];
  report: ComparisonReport;
  editable: boolean;
  onDone: (v: ParallelRunView) => void;
}) {
  const orgId = useOrgId();
  const t = useT();
  const title = reportTitle(meta.key);
  const upload = useMutation({
    mutationFn: async (f: File) => {
      const file = await readUpload(f);
      return api<ParallelRunView>(`/orgs/${orgId}/parallel-runs/${run.id}/figures/${meta.key}`, {
        method: 'PUT',
        body: JSON.stringify({ file }),
      });
    },
    onSuccess: (v) => {
      onDone(v);
      toast(t('reportPages.dashboard.parallelRun.compared', { report: title }));
    },
  });
  const remove = useMutation({
    mutationFn: () =>
      api<ParallelRunView>(`/orgs/${orgId}/parallel-runs/${run.id}/figures/${meta.key}`, {
        method: 'DELETE',
      }),
    onSuccess: onDone,
  });
  return (
    <div
      className="flex flex-col gap-2 rounded-lg border bg-card p-3"
      data-testid={`upload-${meta.key}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium">{title}</span>
        {report.uploaded ? (
          report.differences ? (
            <StatusChip tone="warning">
              {t('reportPages.dashboard.parallelRun.differences', { count: report.differences })}
            </StatusChip>
          ) : (
            <StatusChip tone="success">{t('reportPages.dashboard.parallelRun.agrees')}</StatusChip>
          )
        ) : (
          <StatusChip tone="neutral">
            {t('reportPages.dashboard.parallelRun.notUploaded')}
          </StatusChip>
        )}
      </div>
      <ErrorBanner>{upload.error?.message ?? remove.error?.message}</ErrorBanner>
      {report.uploaded ? (
        <div className="text-xs text-muted-foreground">
          {report.uploaded.fileName} · {formatDateTime(report.uploaded.uploadedAt)}
          {report.uploaded.uploadedBy ? ` · ${report.uploaded.uploadedBy}` : ''}
        </div>
      ) : (
        <div className="text-xs text-muted-foreground">
          {t('reportPages.dashboard.parallelRun.qboWhere', {
            where: t(`reportPages.dashboard.parallelRun.whereInQbo.${meta.key}`),
          })}
        </div>
      )}
      {editable ? (
        report.uploaded ? (
          <div className="flex gap-2">
            <FileReplace busy={upload.isPending} onFile={(f) => upload.mutate(f)} label={title} />
            <Button
              variant="ghost"
              size="sm"
              disabled={remove.isPending}
              onClick={() => remove.mutate()}
            >
              {t('reportPages.dashboard.parallelRun.remove')}
            </Button>
          </div>
        ) : (
          <FileDrop
            compact
            title={t('reportPages.dashboard.parallelRun.uploadTitle')}
            hint={t('reportPages.dashboard.parallelRun.uploadHint')}
            busy={upload.isPending}
            onFile={(f) => upload.mutate(f)}
          />
        )
      ) : null}
    </div>
  );
}

function FileReplace({
  busy,
  onFile,
  label,
}: {
  busy: boolean;
  onFile: (f: File) => void;
  label: string;
}) {
  const t = useT();
  return (
    <label className="inline-flex">
      <span className="sr-only">
        {t('reportPages.dashboard.parallelRun.replaceLabel', { report: label })}
      </span>
      <input
        type="file"
        className="sr-only"
        accept=".csv,.xlsx"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = '';
        }}
      />
      <span
        className={cn(
          'inline-flex h-8 cursor-pointer items-center rounded-md border px-3 text-sm hover:bg-accent',
          busy && 'pointer-events-none opacity-50',
        )}
      >
        {busy
          ? t('reportPages.dashboard.parallelRun.reading')
          : t('reportPages.dashboard.parallelRun.replace')}
      </span>
    </label>
  );
}

function Comparison({
  run,
  report,
  onlyDiff,
  editable,
  onSaved,
}: {
  run: ParallelRunView;
  report: ComparisonReport;
  onlyDiff: boolean;
  editable: boolean;
  onSaved: (v: ParallelRunView) => void;
}) {
  const t = useT();
  const lines = visibleLines(report.lines, onlyDiff);
  return (
    <div className="rounded-lg border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{reportTitle(report.report)}</TableHead>
            <TableHead className="w-[150px] text-end">
              {t('reportPages.dashboard.parallelRun.colQuickBooks')}
            </TableHead>
            <TableHead className="w-[150px] text-end">
              {t('reportPages.dashboard.parallelRun.colOutss')}
            </TableHead>
            <TableHead className="w-[170px] text-end">
              {t('reportPages.dashboard.parallelRun.colDifference')}
            </TableHead>
            <TableHead className="w-[340px]">
              {t('reportPages.dashboard.parallelRun.colExplanation')}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {lines.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="py-6 text-center text-muted-foreground">
                {onlyDiff
                  ? t('reportPages.dashboard.parallelRun.noDifferencesShort')
                  : t('reportPages.dashboard.parallelRun.nothingToCompare')}
              </TableCell>
            </TableRow>
          ) : (
            lines.map((l) => (
              <Line
                key={l.key}
                run={run}
                report={report}
                line={l}
                editable={editable}
                onSaved={onSaved}
              />
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function Line({
  run,
  report,
  line,
  editable,
  onSaved,
}: {
  run: ParallelRunView;
  report: ComparisonReport;
  line: ComparisonLine;
  editable: boolean;
  onSaved: (v: ParallelRunView) => void;
}) {
  const orgId = useOrgId();
  const t = useT();
  const differs = !isZero(line.difference);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(line.note?.text ?? '');
  const save = useMutation({
    mutationFn: () =>
      api<ParallelRunView>(`/orgs/${orgId}/parallel-runs/${run.id}/notes`, {
        method: 'PUT',
        body: JSON.stringify({ report: report.report, lineKey: line.key, note: text }),
      }),
    onSuccess: (v) => {
      onSaved(v);
      setEditing(false);
    },
  });
  return (
    <TableRow className={cn(differs && 'bg-warning/10')} data-difference={differs || undefined}>
      <TableCell className="font-medium">
        {line.label}
        {line.qbo === null ? (
          <div className="text-xs font-normal text-muted-foreground">
            {t('reportPages.dashboard.parallelRun.notInQbo')}
          </div>
        ) : line.outss === null ? (
          <div className="text-xs font-normal text-muted-foreground">
            {t('reportPages.dashboard.parallelRun.notInOutss')}
          </div>
        ) : null}
      </TableCell>
      <TableCell className="tnum text-end">{fmtAmount(line.qbo)}</TableCell>
      <TableCell className="tnum text-end">
        {line.outss !== null && line.drill ? (
          <DrillLink drill={line.drill as Drill} amount>
            {fmtAmount(line.outss)}
          </DrillLink>
        ) : (
          fmtAmount(line.outss)
        )}
      </TableCell>
      <TableCell className="tnum text-end">
        {differs ? (
          <span className="inline-flex items-center gap-2">
            <StatusChip tone="warning">
              {t('reportPages.dashboard.parallelRun.difference')}
            </StatusChip>
            <strong>{fmtAmount(line.difference)}</strong>
          </span>
        ) : (
          <span className="text-muted-foreground">{fmtAmount(line.difference)}</span>
        )}
      </TableCell>
      <TableCell className="text-sm">
        {editing ? (
          <div className="flex flex-col gap-2">
            <ErrorBanner>{save.error?.message}</ErrorBanner>
            <Textarea
              aria-label={t('reportPages.dashboard.parallelRun.explanationFor', {
                line: line.label,
              })}
              value={text}
              maxLength={1000}
              onChange={(e) => setText(e.target.value)}
            />
            <div className="flex gap-2">
              <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
                {t('common.save')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setText(line.note?.text ?? '');
                  setEditing(false);
                }}
              >
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        ) : line.note ? (
          <div>
            <p>{line.note.text}</p>
            <p className="text-xs text-muted-foreground">
              <bdi>
                {line.note.updatedBy ?? t('reportPages.dashboard.parallelRun.anAccountant')}
              </bdi>
              , {formatDateTime(line.note.updatedAt)}
              {editable ? (
                <>
                  {' · '}
                  <button className="text-primary hover:underline" onClick={() => setEditing(true)}>
                    {t('common.edit')}
                  </button>
                </>
              ) : null}
            </p>
          </div>
        ) : differs && editable ? (
          <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
            <MessageSquare className="size-4" /> {t('reportPages.dashboard.parallelRun.explain')}
          </Button>
        ) : differs ? (
          <span className="text-warning">{t('reportPages.dashboard.parallelRun.unexplained')}</span>
        ) : null}
      </TableCell>
    </TableRow>
  );
}
